import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

export default function PhilosophySection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="philosophy"
      ref={sectionRef}
      className="py-16 sm:py-24 md:py-32 px-4 sm:px-8 bg-washi"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8 sm:gap-12 md:gap-16">
          {/* Left Side - Title */}
          <div
            className={`md:col-span-2 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
          >
            <div className="relative">
              <div className="absolute -left-4 sm:-left-6 top-0 bottom-0 w-px bg-indigo"></div>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-sumi mb-4 sm:mb-8 tracking-wide">
                Philosophy<br />& Research
              </h2>
              <h3 className="font-serif text-lg sm:text-xl md:text-2xl text-sumi/90 font-medium mb-4 sm:mb-6">
                有限の中で無限を観る技術
              </h3>
              <Link
                to="/philosophy"
                className="inline-flex items-center gap-2 font-sans text-xs tracking-[0.2em] text-indigo border border-indigo/40 px-5 py-2.5 rounded-full hover:bg-indigo hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap mt-2"
              >
                <span>研究の詳細を読む</span>
                <i className="ri-arrow-right-line text-sm"></i>
              </Link>
            </div>
          </div>

          {/* Right Side - Content */}
          <div
            className={`md:col-span-3 transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <div className="relative">
              {/* Decorative Element */}
              <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-indigo/10"></div>

              <div className="relative space-y-4 sm:space-y-6 font-serif text-sm sm:text-base leading-loose text-sumi/80">
                <p>
                  <strong className="text-sumi font-semibold">離島のミニマリズムから都市の洗練までを、一つの「日本音楽文化」として再定義する先駆的試み。</strong>
                </p>
                <p>
                  日本列島は、海流によって運ばれた多様な文化が交差する場所であったと推測されます。この地理的特性が、独自の音楽表現を育んできました。
                </p>
                <p>
                  当研究所では、歴史への畏敬の念を持ちながら、実践知による身体分析を通じて、日本音楽の構造を現代に再構築しています。それは単なる保存ではなく、創造的な再解釈です。
                </p>
                <p>
                  伝統的な様々な音楽の潮流を統合的に捉え、現代のアート、エンターテインメント、エデュケーションへと実装することで、「島国の感性」を次世代へと継承します。
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}